/**
 * Outil « Triangle SOH-CAH-TOA » (Boîte à outils, 3e → 2nde).
 *
 * Un triangle ABC rectangle en C. On choisit l'angle de référence (Â ou B̂) :
 * côté opposé, côté adjacent et hypoténuse se colorient, et cos, sin, tan se
 * lisent comme rapports de longueurs. Changer la taille ne change pas les
 * rapports. Valeurs exactes à 30°, 45° et 60°. Défi : reconnaître les côtés.
 */

import { useRef, useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  Note,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  ToggleChip,
} from '../../ui/controls';
import { makeFrame } from '../../ui/frame';
import { formatNumber } from '../../ui/format';
import {
  angleArcPath,
  DragHandle,
  INK,
  PlotGrid,
  RightAngleMark,
  FIGURE_BG_CLASS,
} from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  ANGLE_MAX,
  ANGLE_MIN,
  fromPoint,
  HYP_MAX,
  HYP_MIN,
  lengths,
  pickQuestion,
  QUIZ_ROUNDS,
  ratios,
  ROLE_NAMES,
  roleOf,
  sideFor,
  vertices,
  type RefVertex,
  type Role,
  type SideName,
  type SideQuestion,
  type RightTriangle,
} from './right-triangle';

const SIZE = 320;
const VP = { xMin: -0.8, xMax: 9.8, yMin: -0.8, yMax: 9.8 };
const frame = makeFrame(VP, SIZE);
const ROLE_COLORS: Record<Role, string> = {
  adj: 'var(--color-primary-500)',
  opp: 'var(--color-accent-500)',
  hyp: 'var(--color-ink-700)',
};
const SIDES: readonly SideName[] = ['AB', 'AC', 'BC'];

const texNum = (v: number, d = 2) => formatNumber(v, d).replace(',', '{,}');
/** la longueur s'écrit-elle exactement avec 2 décimales ? */
const exact2 = (v: number) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-9;
const ROLE_SHORT: Record<Role, string> = { opp: 'opposé', adj: 'adjacent', hyp: 'hypoténuse' };

interface Quiz {
  round: number;
  q: SideQuestion;
  answer: SideName | null;
  score: number;
}

export function RightTriangleTool({
  onSend,
  sendCost,
  rng = Math.random,
}: ToolProps & { rng?: () => number }) {
  const [tri, setTri] = useState<RightTriangle>({ angleA: 30, hyp: 7 });
  const [ref, setRef] = useState<RefVertex>('A');
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const v = vertices(tri);
  const len = lengths(tri);
  const shownRef = quiz ? quiz.q.ref : ref;
  const reading = ratios(tri, shownRef);
  const hideRoles = quiz !== null && quiz.answer === null;

  const drag = useDrag<'B'>({
    svgRef,
    frame,
    ids: ['B'],
    pointOf: () => v.B,
    keyStep: 0.5,
    onDrag: (_, p) => {
      const next = fromPoint(p);
      if (next) setTri(next);
    },
  });

  const px = (name: 'A' | 'B' | 'C') => ({ x: frame.tx(v[name].x), y: frame.ty(v[name].y) });
  const P = { A: px('A'), B: px('B'), C: px('C') };
  const ends: Record<SideName, ['A' | 'B' | 'C', 'A' | 'B' | 'C']> = {
    AB: ['A', 'B'],
    AC: ['A', 'C'],
    BC: ['B', 'C'],
  };
  const colorOf = (s: SideName) => (hideRoles ? INK : ROLE_COLORS[roleOf(shownRef, s)]);

  // arc de l'angle de référence, entre ses deux côtés
  const other = shownRef === 'A' ? P.B : P.A;
  const arc = angleArcPath(P[shownRef].x, P[shownRef].y, P.C.x, P.C.y, other.x, other.y, 30);

  const startQuiz = () => setQuiz({ round: 1, q: pickQuestion(rng), answer: null, score: 0 });
  const answer = (s: SideName) => {
    if (!quiz || quiz.answer) return;
    const ok = sideFor(quiz.q.ref, quiz.q.role) === s;
    setQuiz({ ...quiz, answer: s, score: quiz.score + (ok ? 1 : 0) });
  };
  const nextQuestion = () => {
    if (!quiz) return;
    setQuiz({
      round: quiz.round + 1,
      q: pickQuestion(rng, quiz.q),
      answer: null,
      score: quiz.score,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {!quiz && (
          <Segmented
            ariaLabel="Angle de référence"
            value={ref}
            options={[
              { value: 'A', label: 'Angle en A' },
              { value: 'B', label: 'Angle en B' },
            ]}
            onChange={setRef}
          />
        )}
        <ToggleChip on={quiz !== null} onClick={() => (quiz ? setQuiz(null) : startQuiz())}>
          Défi
        </ToggleChip>
      </div>

      {quiz && (
        <QuizBanner
          quiz={quiz}
          onAnswer={answer}
          onNext={nextQuestion}
          onRestart={startQuiz}
          onQuit={() => setQuiz(null)}
        />
      )}

      <div className="mx-auto w-full max-w-[24rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`Triangle ABC rectangle en C, angle Â de ${tri.angleA}°`}
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} labels={false} axes={false} />
          <polygon
            points={`${P.A.x},${P.A.y} ${P.B.x},${P.B.y} ${P.C.x},${P.C.y}`}
            fill="var(--color-primary-500)"
            fillOpacity={0.06}
          />
          {SIDES.map((s) => {
            const [p, q] = ends[s];
            return (
              <line
                key={s}
                data-testid={`side-${s}`}
                data-role={hideRoles ? undefined : roleOf(shownRef, s)}
                x1={P[p].x}
                y1={P[p].y}
                x2={P[q].x}
                y2={P[q].y}
                stroke={colorOf(s)}
                strokeWidth={4.5}
                strokeLinecap="round"
              />
            );
          })}
          <RightAngleMark
            x={P.C.x}
            y={P.C.y}
            ux={P.A.x - P.C.x}
            uy={P.A.y - P.C.y}
            vx={P.B.x - P.C.x}
            vy={P.B.y - P.C.y}
          />
          <path d={arc} fill="none" stroke="var(--color-xp-500)" strokeWidth={2.5} />
          <text x={P.A.x - 14} y={P.A.y + 4} fontSize={15} fontWeight={700} fill={INK}>
            A
          </text>
          <text x={P.C.x + 6} y={P.C.y + 16} fontSize={15} fontWeight={700} fill={INK}>
            C
          </text>
          <DragHandle
            x={P.B.x}
            y={P.B.y}
            color={INK}
            label="B"
            labelDx={P.B.x > SIZE - 40 ? -18 : 16}
            labelDy={-12}
            name={`Sommet B : angle Â de ${tri.angleA}°, hypoténuse ${formatNumber(tri.hyp, 1)}`}
            active={drag.active === 'B'}
            keyProps={drag.keyProps('B')}
          />
        </svg>
      </div>

      {!hideRoles && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm" aria-label="Rôle des côtés">
          {(['opp', 'adj', 'hyp'] as const).map((role) => (
            <li key={role} className="text-ink-700 flex items-center gap-2">
              <span
                className="h-3 w-3 rounded-full"
                style={{ background: ROLE_COLORS[role] }}
                aria-hidden
              />
              {ROLE_NAMES[role]} [{sideFor(shownRef, role)}]
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ParamSlider
          name={<MathText>{'$\\widehat{A}$'}</MathText>}
          ariaLabel="Angle Â en degrés"
          value={tri.angleA}
          min={ANGLE_MIN}
          max={ANGLE_MAX}
          step={1}
          format={(d) => `${d}°`}
          onChange={(d) => setTri((t) => ({ ...t, angleA: d }))}
        />
        <ParamSlider
          name="Hypoténuse AB"
          ariaLabel="Longueur de l’hypoténuse AB"
          value={tri.hyp}
          min={HYP_MIN}
          max={HYP_MAX}
          step={0.5}
          format={(h) => formatNumber(h, 1)}
          onChange={(h) => setTri((t) => ({ ...t, hyp: h }))}
        />
      </div>

      {!hideRoles && (
        <section aria-label="Rapports trigonométriques" className="space-y-2">
          {reading.map((r) => (
            <div
              key={r.name}
              data-testid={`ratio-${r.name}`}
              className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
            >
              <div className="flex min-h-11 items-center gap-2">
                <span className="bg-ink-100 text-ink-700 shrink-0 rounded-lg px-2 py-1 text-xs font-bold tracking-wide">
                  {r.mnemonic}
                </span>
                <span className="text-ink-600 min-w-0 flex-1 text-sm">
                  {ROLE_SHORT[r.numRole]} ÷ {ROLE_SHORT[r.denRole]}
                </span>
                {onSend && r.exact && (
                  <SendToSheetButton
                    label={r.exact.plain}
                    cost={sendCost}
                    onClick={() => onSend({ atoms: r.exact!.atoms })}
                  />
                )}
              </div>
              <MathText className="text-ink-900 block overflow-x-auto text-base">
                {`$\\displaystyle \\${r.name} \\widehat{${shownRef}} = \\frac{${r.num}}{${r.den}} ${
                  r.exact ? `= ${r.exact.tex}` : `\\approx ${texNum(r.value, 3)}`
                }$`}
              </MathText>
              <p className="text-ink-500 text-sm">
                {r.num} {exact2(len[r.num]) ? '=' : '≈'} {formatNumber(len[r.num], 2)} et {r.den}{' '}
                {exact2(len[r.den]) ? '=' : '≈'} {formatNumber(len[r.den], 2)} :{' '}
                {formatNumber(len[r.num], 2)} ÷ {formatNumber(len[r.den], 2)}{' '}
                {exact2(len[r.num]) &&
                exact2(len[r.den]) &&
                Math.abs(r.value * 1000 - Math.round(r.value * 1000)) < 1e-9
                  ? '='
                  : '≈'}{' '}
                {formatNumber(r.value, 3)}
              </p>
            </div>
          ))}
        </section>
      )}

      {!quiz && (
        <Note>
          Change l’hypoténuse : les longueurs changent, pas les rapports. Ils ne dépendent que de
          l’angle. À 30°, 45° et 60°, on connaît leurs valeurs exactes.
        </Note>
      )}
    </div>
  );
}

function QuizBanner({
  quiz,
  onAnswer,
  onNext,
  onRestart,
  onQuit,
}: {
  quiz: Quiz;
  onAnswer: (s: SideName) => void;
  onNext: () => void;
  onRestart: () => void;
  onQuit: () => void;
}) {
  const good = sideFor(quiz.q.ref, quiz.q.role);
  const finished = quiz.round >= QUIZ_ROUNDS && quiz.answer !== null;
  const why =
    quiz.q.role === 'hyp'
      ? 'l’hypoténuse est en face de l’angle droit'
      : quiz.q.role === 'adj'
        ? `le côté adjacent touche l’angle ${quiz.q.ref} sans être l’hypoténuse`
        : `le côté opposé ne touche pas l’angle ${quiz.q.ref}`;
  return (
    <section
      aria-live="polite"
      className="bg-xp-50 space-y-2 rounded-2xl p-3"
      data-testid="soh-quiz"
    >
      <p className="text-ink-900 text-sm font-semibold">
        Question {quiz.round}/{QUIZ_ROUNDS} · Pour l’angle{' '}
        <MathText>{`$\\widehat{${quiz.q.ref}}$`}</MathText>, quel est le{' '}
        {quiz.q.role === 'hyp' ? 'côté qui est l’hypoténuse' : ROLE_NAMES[quiz.q.role]} ?
      </p>
      <div className="grid grid-cols-3 gap-2">
        {SIDES.map((s) => (
          <button
            key={s}
            type="button"
            disabled={quiz.answer !== null}
            onClick={() => onAnswer(s)}
            className={cn(
              'min-h-11 rounded-xl border text-sm font-bold active:scale-[0.98]',
              quiz.answer && s === good
                ? 'border-success-500 bg-success-50 text-ink-900'
                : quiz.answer === s
                  ? 'border-error-500 bg-error-50 text-ink-900'
                  : 'border-ink-200 text-ink-800 bg-white',
            )}
          >
            [{s}]
          </button>
        ))}
      </div>
      {quiz.answer !== null && (
        <>
          <p className="text-ink-700 text-sm">
            {quiz.answer === good ? 'Oui : ' : `Non, c’est [${good}] : `}
            {why}.
          </p>
          {finished ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-ink-900 font-bold">
                Score : {quiz.score}/{QUIZ_ROUNDS}
              </p>
              <button type="button" onClick={onRestart} className={PRIMARY_BUTTON}>
                Rejouer
              </button>
              <button type="button" onClick={onQuit} className={SECONDARY_BUTTON}>
                Fin du défi
              </button>
            </div>
          ) : (
            <button type="button" onClick={onNext} className={PRIMARY_BUTTON}>
              Question suivante
            </button>
          )}
        </>
      )}
    </section>
  );
}
